import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { WebLogger } from "@weps/shared-http";
import { ZokService } from "../zok/zok.service";
import { Person, PersonCreate, PersonUpdate } from "../zok";
import { NewBeneficiary, ExistingBeneficiary, PersonUpdateData } from "@models/beneficiary.model";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import { Person as EstateProfilePerson } from "@models/estateProfile.model";

export interface BeneficiaryZokResult {
  success: boolean;
  beneficiary?: ExistingBeneficiary;
  error?: unknown;
}

/**
 * Maps an estate profile Person to ZOK Person format for beneficiary selection.
 * The uuid from estate profile becomes householdMemberId in ZOK format.
 */
function mapEstateProfilePersonToZokPerson(person: EstateProfilePerson): Person {
  return {
    householdMemberId: person.uuid || person.piClientId || "",
    name: {
      firstName: person.firstName,
      lastName: person.lastName,
    },
    birthDate: person.dateOfBirth || "",
    state: person.state,
    deceased: person.deceased,
    lastUpdatedBy: {
      idType: "PROCESS",
    },
  };
}

@Injectable({
  providedIn: "root",
})
export class BeneficiaryZokService {
  private readonly globalStore = inject(GlobalStateStore);
  private householdDataLoaded = new Subject<void>();
  private loadInProgress = false;

  constructor(private zokService: ZokService) {}

  /**
   * Returns an Observable that emits when household data has finished loading.
   * Components should subscribe before calling preloadHouseholdMembers().
   */
  getHouseholdDataLoadedObservable(): Observable<void> {
    return this.householdDataLoaded.asObservable();
  }

  /**
   * Preloads household members from estate profile data and caches in GlobalStateStore.
   * No longer makes direct ZOK calls - uses people from estate profile GraphQL query.
   * Safe to call multiple times - will skip if already loaded or loading.
   */
  preloadHouseholdMembers(): void {
    // Skip if already loaded
    if (this.globalStore.householdPeopleLoaded?.()) {
      this.logEvent("household_preload_skipped_already_loaded");
      this.householdDataLoaded.next();
      return;
    }

    if (this.loadInProgress) {
      this.logEvent("household_preload_skipped_in_progress");
      return;
    }

    this.loadInProgress = true;
    this.logEvent("household_preload_start", {
      source: "estate_profile",
    });

    try {
      // Get people from estate profile (already loaded by ROB service)
      const estateProfile = this.globalStore.estateProfile?.();

      if (!estateProfile) {
        this.logEvent("household_preload_no_estate_profile", {}, "error");
        this.loadInProgress = false;
        this.householdDataLoaded.error(new Error("Estate profile not available"));
        return;
      }

      // Map estate profile people to ZOK Person format
      const allPeople: EstateProfilePerson[] = [
        estateProfile.primaryClient,
        ...(estateProfile.people || []),
      ];

      const zokPeople: Person[] = allPeople
        .filter((p) => p && (p.uuid || p.piClientId)) // Only include people with IDs
        .map(mapEstateProfilePersonToZokPerson);

      this.globalStore.updateHouseholdPeople(zokPeople);
      this.logEvent("household_preload_success", {
        source: "estate_profile",
        people_count: zokPeople.length,
        member_ids: zokPeople.map((p) => p.householdMemberId),
      });
      this.loadInProgress = false;
      this.householdDataLoaded.next();
    } catch (err: unknown) {
      this.globalStore.setHouseholdPeopleError();
      this.logEvent(
        "household_preload_failed",
        {
          source: "estate_profile",
          error_message: (err as Error)?.message || String(err),
        },
        "error",
      );
      this.loadInProgress = false;
      this.householdDataLoaded.error(err);
    }
  }

  /**
   * Refreshes household data from ZOK and updates cache.
   * Call after creating/updating a person to ensure cache is current.
   */
  refreshHouseholdMembers(): void {
    this.globalStore.resetHouseholdPeople();
    this.loadInProgress = false;
    this.preloadHouseholdMembers();
  }

  /**
   * Refreshes household members in the background without blocking UI.
   * Re-maps estate profile data and merges with ZOK-created people to preserve newly added beneficiaries.
   */
  refreshHouseholdMembersInBackground(): void {
    this.logEvent("household_background_refresh_start", {
      source: "estate_profile",
    });

    try {
      // Get people from estate profile
      const estateProfile = this.globalStore.estateProfile?.();

      if (!estateProfile) {
        this.logEvent("household_background_refresh_no_estate_profile", {}, "error");
        this.householdDataLoaded.next();
        return;
      }

      // Map estate profile people to ZOK Person format
      const allPeople: EstateProfilePerson[] = [
        estateProfile.primaryClient,
        ...(estateProfile.people || []),
      ];

      const estateProfilePeople: Person[] = allPeople
        .filter((p) => p && (p.uuid || p.piClientId))
        .map(mapEstateProfilePersonToZokPerson);

      // Get existing cached people (includes ZOK-created people not yet in estate profile)
      const existingPeople = this.globalStore.householdPeople?.() || [];
      const estateProfileIds = new Set(estateProfilePeople.map((p) => p.householdMemberId));

      // Find ZOK-created people that aren't in estate profile yet
      const zokOnlyPeople = existingPeople.filter(
        (p) => !estateProfileIds.has(p.householdMemberId),
      );

      // Merge: estate profile people + ZOK-only people
      const mergedPeople = [...estateProfilePeople, ...zokOnlyPeople];

      this.globalStore.updateHouseholdPeople(mergedPeople);
      this.logEvent("household_background_refresh_success", {
        source: "estate_profile",
        people_count: mergedPeople.length,
        estate_profile_count: estateProfilePeople.length,
        zok_only_count: zokOnlyPeople.length,
      });
      this.householdDataLoaded.next();
    } catch (err: unknown) {
      this.logEvent(
        "household_background_refresh_failed",
        {
          source: "estate_profile",
          error_message: (err as Error)?.message || String(err),
        },
        "error",
      );
      // Don't update error state - keep existing cached data usable
      this.householdDataLoaded.next();
    }
  }

  createPersonInZok(
    bene: NewBeneficiary,
    onSuccess: (bene: ExistingBeneficiary) => void,
    onError: (err: unknown) => void,
  ): void {
    const personCreate: PersonCreate = {
      name: {
        firstName: bene.personData.firstName,
        lastName: bene.personData.lastName,
      },
      birthDate: bene.personData.dateOfBirth,
      state: bene.personData.stateOfResidence,
    };

    this.logEvent("beneficiary_create_person_start", {
      has_state: !!personCreate.state,
    });

    this.zokService.createPerson(personCreate).subscribe({
      next: (response) => {
        const savedBene: ExistingBeneficiary = {
          kind: "existing",
          householdMemberId: response.person.householdMemberId,
          type: bene.type,
          distributionPercentage: bene.distributionPercentage,
          beneficiaryType: bene.beneficiaryType,
        };

        const currentPeople = this.globalStore.householdPeople?.() || [];
        this.globalStore.updateHouseholdPeople([...currentPeople, response.person]);

        this.logEvent("beneficiary_create_person_success", {
          household_member_id: response.person.householdMemberId,
          last_updated_by: response.person.lastUpdatedBy,
        });
        onSuccess(savedBene);
      },
      error: (err) => {
        const zokError = err?.error?.error || err?.error;
        this.logEvent(
          "beneficiary_create_person_failed",
          {
            status_code: err?.status,
            error_message: err?.message,
            zok_error_type: zokError?.type,
            zok_error_message: zokError?.message,
            zok_error_path: zokError?.path,
            zok_error_invalid_value: zokError?.invalidValue,
          },
          "error",
        );
        onError(err);
      },
    });
  }

  updatePersonInZok(
    householdMemberId: string,
    personData: PersonUpdateData,
    householdPeople: Person[],
    onSuccess: () => void,
    onError: (err: unknown) => void,
  ): void {
    const original = householdPeople.find((p) => p.householdMemberId === householdMemberId);

    const { isPrimary: _ip, lastUpdatedBy: _lub, ...baseFields } = original || ({} as Person);
    const personUpdate: PersonUpdate = {
      ...baseFields,
      householdMemberId,
      name: { ...original?.name, firstName: personData.firstName, lastName: personData.lastName },
      birthDate: personData.dateOfBirth,
      state: personData.stateOfResidence,
    };

    this.logEvent("beneficiary_update_person_start", {
      household_member_id: householdMemberId,
    });

    this.zokService.updatePerson(householdMemberId, personUpdate).subscribe({
      next: (response) => {
        const currentPeople = this.globalStore.householdPeople?.() || [];
        const updatedPeople = currentPeople.map((p) =>
          p.householdMemberId === householdMemberId ? response.person : p,
        );
        this.globalStore.updateHouseholdPeople(updatedPeople);

        this.logEvent("beneficiary_update_person_success", {
          household_member_id: response.person.householdMemberId,
        });
        onSuccess();
      },
      error: (err) => {
        const zokError = err?.error?.error || err?.error;
        this.logEvent(
          "beneficiary_update_person_failed",
          {
            household_member_id: householdMemberId,
            status_code: err?.status,
            error_message: err?.message,
            zok_error_type: zokError?.type,
            zok_error_message: zokError?.message,
          },
          "error",
        );
        onError(err);
      },
    });
  }

  /**
   * Gets cached household members if available, otherwise maps from estate profile data.
   * Use this method from BeneficiarySectionComponent for backward compatibility.
   */
  loadHouseholdMembers(
    onSuccess: (people: Person[]) => void,
    onError: (err: unknown) => void,
  ): void {
    // Check if data is already cached in GlobalStateStore
    if (this.globalStore.householdPeopleLoaded?.()) {
      const cachedPeople = this.globalStore.householdPeople?.() || [];

      if (this.globalStore.householdPeopleError?.()) {
        this.logEvent("household_load_using_cached_error");
        onError(new Error("Household data previously failed to load"));
        return;
      }

      this.logEvent("household_load_using_cached", {
        people_count: cachedPeople.length,
      });
      onSuccess(cachedPeople);
      return;
    }

    const existingPeople = this.globalStore.householdPeople?.();
    if (existingPeople && existingPeople.length > 0) {
      this.logEvent("household_load_using_existing_cache", {
        people_count: existingPeople.length,
      });
      onSuccess(existingPeople);
      return;
    }

    this.logEvent("household_load_from_estate_profile");

    try {
      // Get people from estate profile
      const estateProfile = this.globalStore.estateProfile?.();

      if (!estateProfile) {
        this.logEvent("household_load_no_estate_profile", {}, "error");
        onError(new Error("Estate profile not available"));
        return;
      }

      // Map estate profile people to ZOK Person format
      const allPeople: EstateProfilePerson[] = [
        estateProfile.primaryClient,
        ...(estateProfile.people || []),
      ];

      const zokPeople: Person[] = allPeople
        .filter((p) => p && (p.uuid || p.piClientId))
        .map(mapEstateProfilePersonToZokPerson);

      this.globalStore.updateHouseholdPeople(zokPeople);
      this.logEvent("beneficiary_household_loaded", {
        source: "estate_profile",
        people_count: zokPeople.length,
        member_ids: zokPeople.map((p) => p.householdMemberId),
      });
      onSuccess(zokPeople);
    } catch (err: unknown) {
      this.globalStore.setHouseholdPeopleError();
      this.logEvent(
        "beneficiary_household_load_failed",
        {
          source: "estate_profile",
          error_message: (err as Error)?.message || String(err),
        },
        "error",
      );
      onError(err);
    }
  }

  private logEvent(
    eventName: string,
    metadata: Record<string, unknown> = {},
    level: "info" | "error" = "info",
  ): void {
    WebLogger.log({
      message: eventName,
      level,
      payload: { event_type: eventName, ...metadata },
    });
  }
}
